import { FontLoader, FontStyle, SkapaTheme } from '@inter-ikea-kompis/themes';
import type { ITheme } from '@inter-ikea-kompis/types';

import type { TemplateResult } from '../../lib/lit-html/lit-html';
import { html, render } from '../../lib/lit-html/lit-html';
import type IKompisElementPropertyDeclaration from './types/IKompisElementPropertyDeclaration';
import type IKompisElementPropertyValue from './types/IKompisElementPropertyValue';

const fontCache: string[] = [];

function generateFontStyle(theme: ITheme): string {
	const fontFamily = FontStyle.getFontFamily(theme);
	if (!fontCache[fontFamily]) {
		FontLoader.loadFont(theme); // Built in deduplication.
		fontCache[fontFamily] = `
		*, *:before, *:after {
			box-sizing: border-box;
		}
	
		:host {
			font-family: ${fontFamily};
		}`;
	}
	return fontCache[fontFamily];
}

/**
 * Components should extend this class.
 */
export default class KompisElement extends HTMLElement {
	/**
	 * @override
	 */
	public static get observedAttributes(): string[] {
		return Array.from(this.propertyAttributeMap.keys());
	}

	public static propertyConstructor: typeof KompisElement | null = null;
	public static propertyAttributeMap: Map<string, string> = new Map();
	public static propertyDeclarations: Map<string, IKompisElementPropertyDeclaration> = new Map();
	public static requiredProperties: string[] = [];
	public isRendering = false;
	public properties: Map<string, IKompisElementPropertyValue> = new Map();
	public changedProperties: Map<string, IKompisElementPropertyValue> = new Map();
	public shadowRoot: ShadowRoot;
	private updateCallbacks: (() => void)[] = [];
	private updateTimeout: number | NodeJS.Timeout | null = null;

	/**
	 * Class constructor.
	 */
	constructor() {
		super();

		this.attachShadow({
			mode: 'open'
		});
	}

	/**
	 * Requests an update.
	 *
	 * @returns Promise.
	 */
	public requestUpdate(): Promise<void> {
		if (!this.isConnected) {
			return Promise.resolve();
		}
		return new Promise((resolve) => {
			if (this.updateTimeout !== null) {
				clearTimeout(<number>this.updateTimeout);
			}
			this.updateCallbacks.push(resolve);
			this.updateTimeout = setTimeout(() => this.update());
		});
	}

	/**
	 * Requests an update.
	 */
	public cancelUpdate(): void {
		if (this.updateTimeout !== null) {
			clearTimeout(<number>this.updateTimeout);
			this.updateTimeout = null;
		}
		if (this.updateCallbacks.length > 0) {
			const callbacks = this.updateCallbacks;
			this.updateCallbacks = [];
			for (const callback of callbacks) {
				callback();
			}
		}
	}

	/**
	 * Updates the component synchronous.
	 */
	public update(): void {
		this.cancelUpdate();

		if (!this.isConnected) {
			return;
		}

		for (const requiredPropertyName of (<typeof KompisElement>this.constructor)
			.requiredProperties) {
			const value = this.properties.get(requiredPropertyName);
			if (value === undefined || value === null) {
				return;
			}
		}

		const changedProperties = this.changedProperties;
		this.changedProperties = new Map();

		this.isRendering = true;

		if (this.shouldUpdate(changedProperties)) {
			try {
				this.renderToDom();
			} catch (error) {
				this.isRendering = false;
				throw error;
			}
		}

		this.isRendering = false;
	}

	/**
	 * @override
	 */
	public dispatchEvent(event: Event): boolean {
		const result = super.dispatchEvent(event);
		if (
			this['on' + event.type.toLowerCase()] === undefined &&
			this.getAttribute('on' + event.type)
		) {
			eval(this.getAttribute('on' + event.type) ?? '');
		}
		return result;
	}

	/**
	 * Renders the component.
	 *
	 * @returns Template result.
	 */
	protected render(): TemplateResult {
		return undefined;
	}

	/**
	 * Triggered when properties has changed.
	 *
	 * @param _changedProperties Changed properties.
	 * @returns "true" if the component should update.
	 */
	protected shouldUpdate(_changedProperties: Map<string, IKompisElementPropertyValue>): boolean {
		return true;
	}

	/**
	 * Called when the component is inserted to DOM.
	 */
	protected connectedCallback(): void {
		const propertyDeclarations = (<typeof KompisElement>this.constructor).propertyDeclarations;

		this.changedProperties = new Map();

		for (const [propertyName, propertyDeclaration] of Array.from(propertyDeclarations)) {
			const attributeValue = this.getAttribute(propertyName);

			if (attributeValue !== null) {
				this.properties.set(
					propertyName,
					this.parseAttributeValue(propertyDeclaration.type, attributeValue)
				);
			}

			const propertyValue = this.properties.get(propertyName);

			if (propertyValue !== undefined && propertyValue !== propertyDeclaration.defaultValue) {
				this.changedProperties.set(propertyName, propertyDeclaration.defaultValue);
			}
		}

		this.update();
	}

	/**
	 * Called when the component is inserted to DOM.
	 */
	protected disconnectedCallback(): void {
		if (this.updateTimeout !== null) {
			clearTimeout(<number>this.updateTimeout);
			this.updateTimeout = null;
		}
	}

	/**
	 * Attribute changed callback triggered by HTMLElement.
	 *
	 * @param attributeName Attribute name.
	 * @param _oldValue Old value.
	 * @param newValue New value.
	 */
	protected attributeChangedCallback(
		attributeName: string,
		_oldValue: string,
		newValue: string
	): void {
		const propertyName = (<typeof KompisElement>this.constructor).propertyAttributeMap.get(
			attributeName.toLowerCase()
		);

		if (propertyName === undefined) {
			return;
		}

		const propertyDeclaration = (<typeof KompisElement>this.constructor).propertyDeclarations.get(
			propertyName
		);

		if (propertyDeclaration) {
			this[propertyName] = this.parseAttributeValue(propertyDeclaration.type, newValue);
		}
	}

	/**
	 * Renders to dom.
	 */
	private renderToDom(): void {
		render(
			html`<style>
					${generateFontStyle(<ITheme>this.properties.get('theme') || SkapaTheme)}
				</style>
				${this.render()}`,
			this.shadowRoot
		);
	}

	/**
	 * Parses an attribute value.
	 *
	 * @param type Type.
	 * @param attributeValue Attribute value.
	 * @param value
	 * @returns Property value.
	 */
	private parseAttributeValue(
		type: String | Number | Boolean | Object,
		value: string
	): IKompisElementPropertyValue {
		if (value === null) {
			return null;
		}

		switch (type) {
			case String:
				return value;
			case Boolean:
				return value !== null;
			case Number: {
				const parsedValue = Number(value);
				return !isNaN(parsedValue) ? parsedValue : null;
			}
			case Array:
			case Object:
				return JSON.parse(value);
		}

		return null;
	}
}
